import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { toListingRequestRow, validateListingRequest, type ListingRequestInput } from "@/lib/listingRequests";

export function RequestListingDialog({ reportId, title }: { reportId: string; title: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [f, setF] = useState<ListingRequestInput>({ quantity: "", unit: "tons", neededBy: "", deliveryZip: "", note: "" });

  const send = useMutation({
    mutationFn: async () => {
      // The seller is filled in by the database from the report; buyers never see it.
      const { error } = await supabase.from("listing_requests").insert({ ...toListingRequestRow(f), lab_report_id: reportId });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Request sent to the seller");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["my-listing-requests"] });
    },
    onError: (e: Error) => setErr(e.message.includes("duplicate") ? "You already have an open request for this report." : `Couldn't send: ${e.message}`),
  });

  const submit = () => {
    const v = validateListingRequest(f);
    setErr(v);
    if (!v) send.mutate();
  };
  const set = (p: Partial<ListingRequestInput>) => setF((x) => ({ ...x, ...p }));

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); setErr(null); }}>
      <DialogTrigger asChild><Button size="sm">Request a listing</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request a listing</DialogTitle>
          <DialogDescription>Ask the seller to put "{title}" up for sale. They won't see your contact details until you message them.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label htmlFor="lr-qty">Quantity needed</Label><Input id="lr-qty" type="number" min={1} value={f.quantity} onChange={(e) => set({ quantity: e.target.value })} /></div>
          <div><Label>Unit</Label>
            <Select value={f.unit} onValueChange={(v) => set({ unit: v as ListingRequestInput["unit"] })}>
              <SelectTrigger aria-label="Unit"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="tons">Tons</SelectItem><SelectItem value="cubic-yards">Cubic yards</SelectItem></SelectContent>
            </Select></div>
          <div><Label htmlFor="lr-date">Needed by (optional)</Label><Input id="lr-date" type="date" value={f.neededBy} onChange={(e) => set({ neededBy: e.target.value })} /></div>
          <div><Label htmlFor="lr-zip">Delivery ZIP (optional)</Label><Input id="lr-zip" inputMode="numeric" maxLength={5} value={f.deliveryZip} onChange={(e) => set({ deliveryZip: e.target.value.replace(/\D/g, "") })} /></div>
        </div>
        <div><Label htmlFor="lr-note">Note (optional)</Label><Textarea id="lr-note" maxLength={1000} rows={3} value={f.note} onChange={(e) => set({ note: e.target.value })} /></div>
        {err && <p role="alert" className="text-sm text-destructive">{err}</p>}
        <DialogFooter><Button onClick={submit} disabled={send.isPending}>Send request</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
